import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  loginSuccessResponseSchema,
  refreshTokenResponseSchema,
} from '@/api/auth.schema';
import { profileResponseSchema } from '@/api/workspace.schema';
import { parseWith } from '@/lib/api-parse';
import { AuthContextType, AuthUser, type UserTipo } from '@/types/auth';
import { api } from '@/services/api';
import { API_BASE_URL } from '@/config/api';
import { API_ENDPOINTS } from '@/config/api';
import {
  clearAccessToken,
  getAccessToken,
  initializeTokenStorage,
  saveAccessToken,
} from '@/lib/token-storage';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const bootstrapAccessTokenFromCookie = useCallback(async (): Promise<string | null> => {
    const refreshUrl = `${API_BASE_URL.replace(/\/$/, '')}/auth/refresh-token`;
    try {
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'X-Imobdesk-Entry-App': 'admindesk',
        },
        credentials: 'include',
      });
      if (!response.ok) return null;
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) return null;
      const raw = await response.json();
      const data = parseWith(refreshTokenResponseSchema, raw);
      if (!data.accessToken) return null;
      saveAccessToken(data.accessToken);
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch {
      return null;
    }
  }, []);

  // Fetch profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.getParsed(
        API_ENDPOINTS.workspace.profile,
        profileResponseSchema,
      );
      const profile = response.data;
      
      const authUser: AuthUser = {
        id: profile.id,
        email: profile.email,
        name: `${profile.nome || ''} ${profile.sobrenome || ''}`.trim() || profile.email,
        avatar: profile.image,
        celular: profile.celular,
        tipo: profile.tipo as UserTipo,
      };
      
      setUser(authUser);
      const latestAccess = getAccessToken();
      if (latestAccess) {
        setAccessToken(latestAccess);
      }
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
      // Se falhar, limpa a sessão
      clearAccessToken();
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        await initializeTokenStorage();
        let token = getAccessToken();
        if (!token) {
          token = await bootstrapAccessTokenFromCookie();
        }
        if (token) {
          setAccessToken(token);
          await fetchProfile();
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [bootstrapAccessTokenFromCookie, fetchProfile]);

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    const response = await api.postParsed(
      API_ENDPOINTS.auth.login,
      { email, password },
      loginSuccessResponseSchema,
    );

    saveAccessToken(response.accessToken);
    setAccessToken(response.accessToken);

    await fetchProfile();
  }, [fetchProfile]);

  // Logout function
  const logout = useCallback(() => {
    clearAccessToken();
    setAccessToken(null);
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!accessToken,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const defaultAuthContext: AuthContextType = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => { throw new Error('AuthProvider not mounted'); },
  logout: () => {},
};

export function useAuth() {
  const context = useContext(AuthContext);
  return context ?? defaultAuthContext;
}
